/**
 * Lazy-loads Quick Add JS on first interaction with a tile trigger.
 * CSS stays eager so the tile icon is styled on first paint.
 * Injects data provider + custom element scripts once, then re-dispatches
 * the activating click so the modal opens without a second user action.
 *
 * Also warms scripts + parsed tile JSON off the tap path:
 * - IntersectionObserver + requestIdleCallback (mobile / coarse pointer)
 * - pointerenter when (hover: hover) and (pointer: fine) (desktop)
 * - focusin (keyboard), same as before
 */
(function () {
  'use strict';

  const TRIGGER_SELECTOR = '.quick-add-tile__trigger';
  const TILE_SELECTOR = 'quick-add-tile';
  const FINE_POINTER_MQ = '(hover: hover) and (pointer: fine)';
  const IDLE_TIMEOUT_MS = 2000;
  const MAX_IDLE_PARSES = 2;
  const IO_ROOT_MARGIN = '280px 0px';

  const scriptEl = document.currentScript;
  const assets = {
    dataJs: scriptEl?.dataset.dataJs || '',
    tileJs: scriptEl?.dataset.tileJs || '',
  };

  let loadPromise = null;
  let isReady = false;

  /** @type {WeakSet<Element>} */
  const queuedTiles = new WeakSet();

  /** @type {Element[]} */
  const idleParseQueue = [];

  /** @type {number|null} */
  let idleHandle = null;

  const finePointerMQ = window.matchMedia(FINE_POINTER_MQ);

  /**
   * @param {Node|null|undefined} node
   * @returns {node is Element}
   */
  function isElement(node) {
    return Boolean(node) && node.nodeType === 1;
  }

  /**
   * @param {EventTarget|null} target
   * @returns {HTMLButtonElement|null}
   */
  function findTrigger(target) {
    if (!isElement(target)) return null;

    if (target.matches(TRIGGER_SELECTOR)) {
      return /** @type {HTMLButtonElement} */ (target);
    }

    const trigger = target.closest(TRIGGER_SELECTOR);
    if (trigger) {
      return /** @type {HTMLButtonElement} */ (trigger);
    }

    const tile = target.closest(TILE_SELECTOR);
    if (tile) {
      return tile.querySelector(TRIGGER_SELECTOR);
    }

    return null;
  }

  /**
   * @param {string} src
   * @returns {Promise<void>}
   */
  function loadScript(src) {
    return new Promise((resolve, reject) => {
      if (!src || document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = false;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`[quick-add-tile-loader] failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  /**
   * @returns {Promise<void>}
   */
  function loadQuickAddAssets() {
    if (isReady) {
      return Promise.resolve();
    }

    if (!loadPromise) {
      loadPromise = (async () => {
        await loadScript(assets.dataJs);
        await loadScript(assets.tileJs);
        await customElements.whenDefined('quick-add-tile');
        isReady = true;
      })().catch((error) => {
        loadPromise = null;
        throw error;
      });
    }

    return loadPromise;
  }

  /**
   * @param {IdleDeadline} [deadline]
   */
  function drainIdleParses(deadline) {
    let n = 0;
    while (idleParseQueue.length && n < MAX_IDLE_PARSES) {
      if (
        deadline &&
        typeof deadline.timeRemaining === 'function' &&
        deadline.timeRemaining() <= 0 &&
        !deadline.didTimeout
      ) {
        break;
      }

      const tile = idleParseQueue.shift();
      if (tile && typeof (/** @type {any} */ (tile).warmup) === 'function') {
        /** @type {any} */ (tile).warmup();
      }
      n += 1;
    }

    if (idleParseQueue.length) scheduleIdleDrain();
  }

  function scheduleIdleDrain() {
    if (idleHandle != null) return;

    if (typeof window.requestIdleCallback === 'function') {
      idleHandle = window.requestIdleCallback(
        (deadline) => {
          idleHandle = null;
          drainIdleParses(deadline);
        },
        { timeout: IDLE_TIMEOUT_MS },
      );
      return;
    }

    idleHandle = window.setTimeout(() => {
      idleHandle = null;
      drainIdleParses();
    }, 0);
  }

  /**
   * Loads Quick Add JS (once) then parses this tile's JSON.
   * @param {Element} tile
   * @param {{ immediate?: boolean }} [options]
   */
  function queueTileWarmup(tile, options = {}) {
    if (!isElement(tile) || queuedTiles.has(tile)) return;
    queuedTiles.add(tile);

    const immediate = Boolean(options.immediate);

    loadQuickAddAssets()
      .then(() => {
        if (immediate && typeof (/** @type {any} */ (tile).warmup) === 'function') {
          /** @type {any} */ (tile).warmup();
          return;
        }

        idleParseQueue.push(tile);
        scheduleIdleDrain();
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * @param {Event} event
   */
  function handleClick(event) {
    const trigger = findTrigger(event.target);
    if (!trigger || isReady) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    loadQuickAddAssets()
      .then(() => {
        trigger.click();
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * @param {FocusEvent} event
   */
  function handleFocusIn(event) {
    const trigger = findTrigger(event.target);
    if (!trigger) return;

    const tile = trigger.closest(TILE_SELECTOR);
    if (tile) {
      queueTileWarmup(tile, { immediate: true });
      return;
    }

    loadQuickAddAssets().catch((error) => {
      console.error(error);
    });
  }

  /**
   * Desktop hover analog. Skipped on coarse pointers so a tap does not
   * count this work toward that interaction's INP.
   * @param {PointerEvent} event
   */
  function handlePointerEnter(event) {
    if (!finePointerMQ.matches) return;
    if (!isElement(event.target)) return;

    const tile = event.target.closest(TILE_SELECTOR);
    if (!tile) return;

    queueTileWarmup(tile, { immediate: true });
  }

  /** @type {IntersectionObserver|null} */
  let intersectionObserver = null;

  /**
   * @param {Element} tile
   */
  function observeTile(tile) {
    if (!intersectionObserver || !isElement(tile)) return;
    intersectionObserver.observe(tile);
  }

  /**
   * @param {Element} tile
   */
  function unobserveTile(tile) {
    if (!intersectionObserver || !isElement(tile)) return;
    intersectionObserver.unobserve(tile);
  }

  function observeExistingTiles() {
    const tiles = document.querySelectorAll(TILE_SELECTOR);
    for (const tile of tiles) {
      observeTile(tile);
    }
  }

  function startViewportWarmup() {
    if (intersectionObserver || typeof IntersectionObserver !== 'function') return;

    intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          queueTileWarmup(entry.target);
        }
      },
      { rootMargin: IO_ROOT_MARGIN },
    );

    observeExistingTiles();

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!isElement(node)) continue;
          if (node.matches(TILE_SELECTOR)) {
            observeTile(node);
            continue;
          }
          const nested = node.querySelectorAll?.(TILE_SELECTOR);
          if (!nested) continue;
          for (const tile of nested) {
            observeTile(tile);
          }
        }

        for (const node of mutation.removedNodes) {
          if (!isElement(node)) continue;
          if (node.matches(TILE_SELECTOR)) {
            unobserveTile(node);
            continue;
          }
          const nested = node.querySelectorAll?.(TILE_SELECTOR);
          if (!nested) continue;
          for (const tile of nested) {
            unobserveTile(tile);
          }
        }
      }
    });

    mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener('click', handleClick, true);
  document.addEventListener('focusin', handleFocusIn, true);
  document.addEventListener('pointerenter', handlePointerEnter, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startViewportWarmup, { once: true });
  } else {
    startViewportWarmup();
  }

  /** @type {any} */ (window).quickAddTileLoaderTestUtils = {
    findTrigger,
    loadQuickAddAssets,
    queueTileWarmup,
    handlePointerEnter,
    handleFocusIn,
    startViewportWarmup,
    finePointerMQ,
    FINE_POINTER_MQ,
    MAX_IDLE_PARSES,
  };
})();
